import { Check, ChevronRight, Lock, Plus, Shield, X } from "lucide-react";
import { useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaSearchInput } from "@/shared/components/forms/ColocaSearchInput";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { toastApiError } from "@/shared/utils/toast";

import { useAccessCan } from "../../hooks/useAccessCan";
import { useApiKeysData, usePoliciesData, useRolesData, useUsersData } from "../../hooks/useAccessData";
import { useRoleMembers } from "../../hooks/useRoleMembers";
import type { AccessRole } from "../../types/access.types";
import { roleCell } from "../../utils/permissionCells";
import { roleColor } from "../../utils/roleColor";
import { SettingsSection } from "../SettingsSection";
import { AccessAreaCatalog } from "./AccessAreaCatalog";
import { AccessScopeBadge } from "./AccessScopeBadge";
import { RoleEditorDrawer } from "./RoleEditorDrawer";

/**
 * "Roles" section of Access & permissions. A role bundles policies and is assigned to users and API keys.
 * Lists the organization's roles and the combined global ones as cards, and the ~86 one-permission base roles
 * by area (`AccessAreaCatalog`), then opens the editor to create/edit/view them. Protected roles — and global
 * roles for org users — are read-only (mirrors the backend `assertWritable`). Gating: read `roles:read`;
 * create `roles:write`; edit `roles:update`; delete `roles:delete`.
 */
export function RolesSection() {
  const { t } = useTranslations("settings");
  const { isInternalUser } = useAuth();
  const { canAll } = useAccessCan();

  const { roles, isLoading, hasNoAccess, load } = useRolesData();
  const { policies } = usePoliciesData();
  // Member counts now ride on the role itself (`member_count`), so there is no per-principal aggregation
  // left to do here. `loadUsers` is still needed: deleting a role cascades the unassignment server-side,
  // which makes the users page's `rolesByUser` stale.
  const { load: loadUsers } = useUsersData();
  const { load: loadKeys } = useApiKeysData();

  const [editor, setEditor] = useState<{ role: AccessRole | null } | null>(null);

  const canCreate = canAll("roles:write");
  // `:write`, not `:update` — `scopes[]` carries RBAC atoms, and the guard collapses `update` to `write`.
  const canUpdate = canAll("roles:write");
  const canDelete = canAll("roles:delete");

  // A role is writable when it is not protected and is in the caller's scope:
  // org users can only touch their own org's roles; internal staff can also edit
  // global (non-protected) ones.
  const roleEditable = (role: AccessRole): boolean =>
    !role.is_protected && (isInternalUser || !!role.organization_id);

  const [query, setQuery] = useState("");
  const term = query.trim().toLowerCase();
  const matches = (role: AccessRole) =>
    !term ||
    role.name.toLowerCase().includes(term) ||
    role.slug.toLowerCase().includes(term) ||
    (role.description?.toLowerCase().includes(term) ?? false);

  // The seed's ~86 one-permission roles are read by area instead of as one card each; the organization's
  // own roles and the global roles that combine several permissions keep their cards.
  const basic = useMemo(() => roles.filter((r) => !r.organization_id && roleCell(r) !== null), [roles]);
  const combined = useMemo(() => roles.filter((r) => !r.organization_id && roleCell(r) === null), [roles]);
  const orgs = useMemo(() => roles.filter((r) => !!r.organization_id), [roles]);
  const shownCombined = combined.filter(matches);
  const shownOrgs = orgs.filter(matches);

  const reload = () => {
    // The catalog carries `member_count`, so reloading it refreshes the tallies too.
    load().catch(toastApiError);
    // Not fire-and-forget: a role delete cascades to BOTH `rbac_user_roles` and `rbac_api_key_roles`, so
    // either principal table is stale until these land, and silently keeping them would present
    // pre-delete assignments as authoritative.
    loadUsers().catch(toastApiError);
    loadKeys().catch(toastApiError);
  };

  const editorRole = editor?.role ?? null;
  const editorReadOnly = editorRole ? !canUpdate || !roleEditable(editorRole) : false;
  // Fetched only while the editor is open, and only for users — see the hook's note on why API-key
  // holders cannot be listed at all.
  const memberPreview = useRoleMembers(editorRole?.id ?? null);

  return (
    <SettingsSection
      id="roles"
      eyebrow={t("access_eyebrow")}
      title={t("access_roles_title")}
      sub={t("access_roles_sub")}
      headerRight={
        canCreate ? (
          <ColocaButton variant="primary" size="sm" icon={Plus} onClick={() => setEditor({ role: null })}>
            {t("access_roles_create")}
          </ColocaButton>
        ) : undefined
      }
    >
      {hasNoAccess ? (
        <ColocaEmpty
          surface="card"
          icon={Lock}
          title={t("access_no_access_title")}
          description={t("access_no_access_description")}
        />
      ) : isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ColocaSkeleton className="h-28 w-full" />
          <ColocaSkeleton className="h-28 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <ColocaSearchInput
            value={query}
            onValueChange={setQuery}
            placeholder={t("access_catalog_search_placeholder")}
          />
          <RoleGroup
            title={t("access_roles_group_org")}
            count={orgs.length}
            note={orgs.length ? t("access_roles_group_org_note") : t("access_roles_group_org_empty")}
            roles={shownOrgs}
            onOpen={(role) => setEditor({ role })}
          />
          {shownCombined.length > 0 && (
            <RoleGroup
              title={t("access_roles_group_combined")}
              count={combined.length}
              note={t("access_roles_group_combined_note")}
              roles={shownCombined}
              onOpen={(role) => setEditor({ role })}
            />
          )}
          {basic.length > 0 && (
            <div>
              <GroupHeader
                title={t("access_roles_group_basic")}
                count={basic.length}
                note={t("access_roles_group_basic_note")}
              />
              <AccessAreaCatalog
                items={basic}
                cellOf={roleCell}
                getId={(role) => role.id}
                describe={(role) =>
                  isInternalUser
                    ? `${role.name} · ${t("access_role_member_count_platform", { count: role.member_count })}`
                    : `${role.name} · ${t("access_role_member_count_org", { count: role.member_count })}`
                }
                onOpen={(role) => setEditor({ role })}
                query={query}
              />
            </div>
          )}
        </div>
      )}

      <RoleEditorDrawer
        open={!!editor}
        onOpenChange={(open) => {
          if (!open) setEditor(null);
        }}
        role={editorRole}
        readOnly={editorReadOnly}
        policies={policies}
        memberPreview={memberPreview}
        canDelete={editorRole ? canDelete && roleEditable(editorRole) : false}
        onSaved={() => {
          setEditor(null);
          reload();
        }}
        onDeleted={() => {
          setEditor(null);
          reload();
        }}
      />
    </SettingsSection>
  );
}

interface RoleGroupProps {
  title: string;
  count: number;
  note: string;
  roles: readonly AccessRole[];
  onOpen: (role: AccessRole) => void;
}

function GroupHeader({ title, count, note }: { title: string; count: number; note: string }) {
  return (
    <div className="mb-2.5 flex flex-wrap items-baseline gap-2 border-b border-dashed border-border pb-2">
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.06em] text-foreground">
        {title}
      </span>
      <ColocaBadge variant="secondary">{count}</ColocaBadge>
      <span className="ml-auto text-[11.5px] text-muted-foreground">{note}</span>
    </div>
  );
}

function RoleGroup({ title, count, note, roles, onOpen }: RoleGroupProps) {
  const { t } = useTranslations("settings");

  return (
    <div>
      <GroupHeader title={title} count={count} note={note} />
      {roles.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-muted/30 px-4 py-5 text-center text-[12.5px] text-muted-foreground">
          {t("access_roles_empty_group")}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {roles.map((role) => (
            <RoleCard key={role.id} role={role} memberCount={role.member_count} onOpen={() => onOpen(role)} />
          ))}
        </div>
      )}
    </div>
  );
}

function RoleCard({
  role,
  memberCount,
  onOpen,
}: {
  role: AccessRole;
  memberCount: number;
  onOpen: () => void;
}) {
  const { t } = useTranslations("settings");
  const { isInternalUser } = useAuth();
  const hasDeny = role.policies.some((p) => p.effect === "deny");
  const visiblePolicies = role.policies.slice(0, 4);
  const extra = role.policies.length - visiblePolicies.length;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="relative flex flex-col gap-2.5 rounded-md border border-border bg-card p-3.5 text-left transition-colors hover:border-border/70 hover:shadow-xs"
    >
      <span
        className="absolute bottom-0 left-0 top-0 w-[3px] rounded-l-md"
        style={{ background: roleColor(role.slug) }}
        aria-hidden
      />
      <div className="flex items-center gap-2 pl-1.5">
        <span
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ background: roleColor(role.slug) }}
          aria-hidden
        />
        <span className="text-[13.5px] font-semibold text-foreground">{role.name}</span>
        {role.is_protected && <Lock size={11} className="text-muted-foreground/70" />}
        {role.is_default && <ColocaBadge variant="secondary">{t("access_role_default_badge")}</ColocaBadge>}
        <ChevronRight size={14} className="ml-auto text-muted-foreground/60" />
      </div>

      <div className="pl-1.5">
        <AccessScopeBadge organizationId={role.organization_id} />
      </div>

      {role.description && (
        <div className="min-h-[28px] pl-1.5 text-[11.5px] leading-snug text-muted-foreground">
          {role.description}
        </div>
      )}

      <div className="flex min-h-[22px] flex-wrap gap-1 pl-1.5">
        {visiblePolicies.map((policy) => (
          <span
            key={policy.id}
            className={cn(
              "inline-flex items-center gap-1 rounded-sm border px-1.5 py-px font-mono text-[9.5px]",
              policy.effect === "deny"
                ? "border-destructive/40 bg-destructive/10 text-destructive"
                : "border-primary/30 bg-primary/10 text-primary",
            )}
          >
            {policy.effect === "deny" ? <X size={8} /> : <Check size={8} />}
            {policy.name}
          </span>
        ))}
        {extra > 0 && <ColocaBadge variant="secondary">+{extra}</ColocaBadge>}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-dashed border-border pt-2 pl-1.5">
        <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-muted-foreground">
          <Shield size={10} />
          {role.policies.length === 1
            ? t("access_role_policy_count_singular")
            : t("access_role_policy_count_plural", { count: role.policies.length })}
        </span>
        {hasDeny && (
          <span className="font-mono text-[10px] font-semibold text-destructive">
            · {t("access_role_has_deny")}
          </span>
        )}
        {/* Tenancy-aware on purpose: the backend scopes `member_count` to the caller's organization, so a
            global role shows a different (and equally correct) number to staff and to an org admin. An
            unqualified "N assigned" would be false for one of the two readers. */}
        <span className="ml-auto font-mono text-[10.5px] text-muted-foreground">
          {isInternalUser
            ? memberCount === 1
              ? t("access_role_member_count_platform_one")
              : t("access_role_member_count_platform", { count: memberCount })
            : memberCount === 1
              ? t("access_role_member_count_org_one")
              : t("access_role_member_count_org", { count: memberCount })}
        </span>
      </div>
    </button>
  );
}
